import './OTP.css';
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { verifyOTP, firstRegistration } from '../../redux/slices/authSlice';

function OTP() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { userType, phone, isLoading, error, otpVerified } = useSelector((state) => state.auth);
    
    const [otp, setOtp] = useState(['', '', '', '']);
    const [timeLeft, setTimeLeft] = useState(60);
    const [canResend, setCanResend] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const inputRefs = useRef([]);

    const slides = [
        {
            title: "منصة علمني التعليمية",
            content: "أكاديميتنا العملية تهدف الى تزويد المتعلمين بالمهارات العملية والتطبيقية التي تؤهلهم لسوق العمل من خلال برامج تدريبية متطورة وتجربة تعليمية تفاعلية."
        },
        {
            title: "تعلم بطريقة ممتعة",
            content: "نقدم محتوى تعليمي تفاعلي وممتع يساعد الطلاب على فهم المفاهيم بسهولة وتطبيقها في الحياة العملية."
        },
        {
            title: "محتوى عالي الجودة",
            content: "محتوى تعليمي متميز ومحدث باستمرار لضمان حصول الطلاب على أفضل تجربة تعليمية ممكنة."
        }
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, 4000);

        return () => clearInterval(interval);
    }, [slides.length]);

    // إضافة انيميشن للعناصر عند التحميل
    useEffect(() => {
        const elements = document.querySelectorAll('.login-card, .login-info-section');
        elements.forEach((el, index) => {
            el.style.animationDelay = `${index * 0.2}s`;
        });

        // إضافة انيميشن للعناصر الداخلية
        const innerElements = document.querySelectorAll('.login-card-title, .login-card-subtitle, .otp-inputs, .login-btn, .resend-section, .login-back-link');
        innerElements.forEach((el, index) => {
            el.style.animationDelay = `${(index + 2) * 0.1}s`;
        });

        // إضافة انيميشن للعناصر المعلوماتية
        const infoElements = document.querySelectorAll('.login-brand, .login-info-slider, .login-indicators');
        infoElements.forEach((el, index) => {
            el.style.animationDelay = `${(index + 1) * 0.2}s`;
        });
    }, []);

    // Redirect if no phone number
    useEffect(() => {
        if (!phone) {
            navigate('/registerPhone');
        }
    }, [phone, navigate]);

    // Redirect if OTP verified
    useEffect(() => {
        if (otpVerified) {
            toast.success('تم التحقق من الرمز بنجاح! 🎉');
            navigate('/registerData');
        }
    }, [otpVerified, navigate]);

    // Show error toast
    useEffect(() => {
        if (error) {
            toast.error(error);
        }
    }, [error]);

    // Timer countdown
    useEffect(() => {
        if (timeLeft > 0) {
            const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
            return () => clearTimeout(timer);
        } else {
            setCanResend(true);
        }
    }, [timeLeft]);

    const handleOtpChange = (index, value) => {
        if (value.length <= 1 && /^\d*$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);

            // Move to next input if value entered
            if (value && index < 3) {
                inputRefs.current[index + 1]?.focus();
            }
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleSubmit = () => {
        const otpString = otp.join('');
        if (otpString.length === 4) {
            toast.info('جاري التحقق من الرمز...');
            dispatch(verifyOTP({ 
                phone: phone,
                code: otpString 
            }));
        }
    };

    const handleResend = () => {
        if (canResend) {
            toast.info('جاري إعادة إرسال الرمز...');
            dispatch(firstRegistration({ phone: phone }));
            setTimeLeft(60);
            setCanResend(false);
            setOtp(['', '', '', '']);
            inputRefs.current[0]?.focus();
        }
    };

    const getUserTypeText = () => {
        switch (userType) {
            case 'student': return 'طالب';
            case 'teacher': return 'معلم';
            case 'parent': return 'ولي أمر';
            default: return '';
        }
    };

    const formatPhone = (phoneNumber) => {
        if (phoneNumber.length > 10) {
            return `${phoneNumber.slice(0, 4)}***${phoneNumber.slice(-4)}`;
        }
        return phoneNumber;
    };

    return (
        <div className="login-page otp-page">
            <div className="login-overlay">
                <div className="login-wrapper">
                    {/* Right Info */}
                    <div className="login-info-section">
                        <h1 className="login-brand">علمني <i className="login-icon-book"></i></h1>

                        <div className="login-info-slider">
                            {slides.map((slide, index) => (
                                <div
                                    key={index}
                                    className={`login-slide ${index === currentSlide ? 'active' : ''}`}
                                >
                                    <h3 className="login-info-title">{slide.title}</h3>
                                    <p className="login-info-paragraph">{slide.content}</p>
                                </div>
                            ))}
                        </div>

                        {/* Indicators */}
                        <div className="login-indicators">
                            {slides.map((_, index) => (
                                <span
                                    key={index}
                                    className={index === currentSlide ? 'active' : ''}
                                    onClick={() => setCurrentSlide(index)}
                                    style={{ cursor: 'pointer' }}
                                ></span>
                            ))}
                        </div>
                    </div>

                    {/* Left Form */}
                    <div className="login-card otp-card">
                        <h2 className="login-card-title">رمز التحقق</h2>
                        <p className="login-card-subtitle">
                            أدخل الرمز المكون من 4 أرقام المرسل إلى {formatPhone(phone)}
                        </p>

                        {/* User Type Display */}
                        <div className="user-type-display">
                            <span className="user-type-label">نوع الحساب:</span>
                            <span className="user-type-value">{getUserTypeText()}</span>
                        </div>

                        {/* OTP Inputs */}
                        <div className="otp-container">
                            <div className="otp-inputs" dir='ltr'>
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={(el) => (inputRefs.current[index] = el)}
                                        type="text"
                                        className={`otp-input ${digit ? 'filled' : ''} ${error ? 'error' : ''}`}
                                        value={digit}
                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                        maxLength={1}
                                        autoFocus={index === 0}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="error-message">
                                {error}
                            </div>
                        )}

                        {/* Resend Section */}
                        <div className="resend-section">
                            <p className="resend-text">
                                لم يصلك الرمز؟
                            </p>
                            <button
                                className={`resend-btn ${!canResend ? 'disabled' : ''}`}
                                onClick={handleResend}
                                disabled={!canResend}
                            >
                                {canResend ? 'إعادة إرسال الرمز' : `إعادة الإرسال خلال ${timeLeft}s`}
                            </button>
                        </div>

                        {/* Button */}
                        <button 
                            className={`login-btn ${otp.join('').length !== 4 || isLoading ? 'disabled' : ''}`}
                            onClick={handleSubmit}
                            disabled={otp.join('').length !== 4 || isLoading}
                        >
                            {isLoading ? 'جاري التحقق...' : 'تحقق من الرمز'}
                        </button>

                        {/* Back Button */}
                        <button 
                            className="back-btn my-3"
                            onClick={() => navigate('/registerPhone')}
                        >
                            ← العودة
                        </button>

                        <a href="/" className="login-back-link">العودة للصفحة الرئيسية</a>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default OTP; 